import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface Comment {
  id: string;
  memberId: string | null;
  displayName: string;
  team: 'black' | 'white' | null;
  message: string;
  createdAt: number;
}

function dbRowToComment(row: Record<string, unknown>): Comment {
  return {
    id: row.id as string,
    memberId: (row.member_id as string) ?? null,
    displayName: row.display_name as string,
    team: (row.team as 'black' | 'white') ?? null,
    message: row.message as string,
    createdAt: row.created_at as number,
  };
}

export function useComments() {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const broadcastChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const myCommentIdsRef = useRef<Set<string>>(new Set());
  const latestCreatedAtRef = useRef<number>(0);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('comments')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);
    if (data) {
      const mapped = data.map(r => dbRowToComment(r as Record<string, unknown>));
      setComments(mapped);
      if (mapped.length > 0) {
        latestCreatedAtRef.current = mapped[0].createdAt;
      }
    }
    setIsLoading(false);
  }, []);

  // Initial load
  useEffect(() => {
    load();
  }, [load]);

  // Polling fallback — 每 10 秒拉一次最新留言，防止 realtime 斷線時留言卡住
  useEffect(() => {
    const poll = async () => {
      const { data } = await supabase
        .from('comments')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      if (!data) return;
      const mapped = data.map(r => dbRowToComment(r as Record<string, unknown>));
      // 只有資料有變化才更新（比對最新一筆的 createdAt）
      const newLatest = mapped.length > 0 ? mapped[0].createdAt : 0;
      if (newLatest !== latestCreatedAtRef.current) {
        latestCreatedAtRef.current = newLatest;
        setComments(mapped);
      }
    };
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, []);

  // Realtime subscription with auto-reconnect
  useEffect(() => {
    const subscribe = () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }

      const channel = supabase
        .channel(`comments-realtime-${Date.now()}`)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'comments' },
          (payload) => {
            const c = dbRowToComment(payload.new as Record<string, unknown>);
            setComments(prev => {
              if (prev.find(x => x.id === c.id)) return prev;
              return [c, ...prev].slice(0, 50);
            });

            // 不是自己發的留言 → 跳出通知
            // 系統留言用 DB 作為 broadcast 的備援（NotificationToast 內建去重，不會重複顯示）
            if (!myCommentIdsRef.current.has(c.id)) {
              if (c.memberId === 'system') {
                // 系統通知：type='system'，導航到對應區塊
                window.dispatchEvent(new CustomEvent('srs-notification', {
                  detail: {
                    id: `comment-${c.id}`,
                    type: 'system',
                    displayName: c.displayName,
                    team: c.team,
                    message: c.message,
                    timestamp: c.createdAt,
                  },
                }));
              } else {
                // 玩家留言：type='comment'
                window.dispatchEvent(new CustomEvent('srs-notification', {
                  detail: {
                    id: `comment-${c.id}`,
                    type: 'comment',
                    displayName: c.displayName,
                    team: c.team,
                    message: c.message,
                    timestamp: c.createdAt,
                  },
                }));
              }
            }
          }
        )
        .on(
          'postgres_changes',
          { event: 'DELETE', schema: 'public', table: 'comments' },
          (payload) => {
            const deletedId = (payload.old as Record<string, unknown>).id as string;
            setComments(prev => prev.filter(c => c.id !== deletedId));
          }
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            setTimeout(() => {
              load();
              subscribe();
            }, 3000);
          }
        });

      channelRef.current = channel;
    };

    subscribe();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [load]);

  // Sys comment broadcast channel — instant system notification delivery
  useEffect(() => {
    const bc = supabase.channel('srs-sys-comment-broadcast', {
      config: { broadcast: { ack: false } },
    });
    bc
      .on('broadcast', { event: 'sys_comment' }, (msg) => {
        const { id, message, team, createdAt } = msg.payload as {
          id: string;
          message: string;
          team: 'black' | 'white' | null;
          createdAt: number;
        };
        const comment: Comment = {
          id,
          memberId: 'system',
          displayName: '⚡系統',
          team,
          message,
          createdAt,
        };
        setComments(prev => {
          if (prev.find(x => x.id === comment.id)) return prev;
          return [comment, ...prev].slice(0, 50);
        });
        // Dispatch system notification immediately
        window.dispatchEvent(new CustomEvent('srs-notification', {
          detail: {
            id: `comment-${comment.id}`,
            type: 'system',
            displayName: comment.displayName,
            team: comment.team,
            message: comment.message,
            timestamp: comment.createdAt,
          },
        }));
      })
      .subscribe();

    return () => { supabase.removeChannel(bc); };
  }, []);

  // Broadcast channel — 監聽 comments_cleared 立即清空（避免等待大量 DELETE realtime 事件）
  useEffect(() => {
    // 使用獨立 channel 名稱，避免與 ScoreContext 的 srs-game-broadcast 衝突
    const bc = supabase.channel('srs-comments-game-events', { config: { broadcast: { ack: false } } });
    bc
      .on('broadcast', { event: 'comments_cleared' }, () => {
        setComments([]);
      })
      .on('broadcast', { event: 'game_reset' }, () => {
        setComments([]);
      })
      .subscribe();

    broadcastChannelRef.current = bc;

    return () => {
      supabase.removeChannel(bc);
      broadcastChannelRef.current = null;
    };
  }, []);

  // Re-fetch when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        load();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [load]);

  const addComment = useCallback(async (
    displayName: string,
    message: string,
    team: 'black' | 'white' | null,
    memberId: string | null,
  ) => {
    const newComment: Comment = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      memberId,
      displayName,
      team,
      message: message.trim(),
      createdAt: Date.now(),
    };
    // 標記為自己的留言，realtime 收到時不觸發通知
    myCommentIdsRef.current.add(newComment.id);
    // Optimistic update
    setComments(prev => {
      if (prev.find(x => x.id === newComment.id)) return prev;
      return [newComment, ...prev].slice(0, 50);
    });
    await supabase.from('comments').insert({
      id: newComment.id,
      member_id: newComment.memberId,
      display_name: newComment.displayName,
      team: newComment.team,
      message: newComment.message,
      created_at: newComment.createdAt,
    });
  }, []);

  const clearAllComments = useCallback(async () => {
    // 本機立即清空
    setComments([]);
    // 廣播給其他裝置立即清空（比等待大量 DELETE realtime 事件快）
    const bc = broadcastChannelRef.current;
    if (bc) {
      await bc.send({ type: 'broadcast', event: 'comments_cleared', payload: {} });
    }
    // 實際刪除資料庫資料
    await supabase.from('comments').delete().gte('created_at', 0);
  }, []);

  return { comments, isLoading, addComment, clearAllComments };
}
